/**
 * Interview Service Layer
 * 
 * Centralized service for all interview-related API operations.
 * All methods use the V2 API client and return typed V2 models.
 */

import { apiClient } from "../lib/api/client";
import type {
    InterviewSetupRequest,
    InterviewSetupResponse,
    SaveAnswerRequest,
    FinalizeInterviewRequest,
    FinalizeInterviewResponse,
    InterviewHistoryResponse,
    InterviewDetail,
    InterviewAnalytics,
    LatestInterviewResponse
} from "../models/v2/interviews";

const HISTORY_STORAGE_KEY = 'pitchme_interview_history';

let _historyCache: InterviewHistoryResponse | null = null;

function readStoredHistory(): InterviewHistoryResponse | null {
    try {
        const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function writeStoredHistory(data: InterviewHistoryResponse) {
    try {
        localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(data));
    } catch {}
}

/**
 * Interview Service
 * 
 * All methods throw ApiException on errors
 */
export const interviewService = {
    /**
     * Setup a new video interview
     * 
     * POST /api/interviews/setup
     * 
     * @param payload - Interview setup data
     * @returns Interview ID and generated questions
     */
    async setupInterview(
        payload: InterviewSetupRequest
    ): Promise<InterviewSetupResponse> {
        return apiClient.post<InterviewSetupResponse>(
            "/api/interviews/setup",
            payload
        );
    },

    /**
     * Setup video interview (legacy method - maps to setupInterview)
     * 
     * @deprecated Use setupInterview instead
     */
    async setupVideoInterview(payload: InterviewSetupRequest): Promise<InterviewSetupResponse> {
        return this.setupInterview(payload);
    },

    /**
     * Save a video answer for a specific question
     * 
     * POST /api/interviews/save-answer
     * 
     * @param payload - Answer data with video URL
     * @returns Success confirmation
     */
    async saveAnswer(payload: SaveAnswerRequest): Promise<{ message: string }> {
        return apiClient.post<{ message: string }>(
            "/api/interviews/save-answer",
            payload
        );
    },

    /**
     * Finalize an interview session
     * 
     * POST /api/interviews/finalize
     * 
     * Triggers final scoring and summary generation
     * 
     * @param payload - Interview ID to finalize
     * @returns Overall score and AI summary
     */
    async finalizeInterview(
        payload: FinalizeInterviewRequest
    ): Promise<FinalizeInterviewResponse> {
        return apiClient.post<FinalizeInterviewResponse>(
            "/api/interviews/finalize",
            payload
        );
    },

    /**
     * Finalize interview (legacy method name)
     * 
     * @deprecated Use finalizeInterview instead
     */
    async finalize_interview(payload: FinalizeInterviewRequest): Promise<FinalizeInterviewResponse> {
        return this.finalizeInterview(payload);
    },

    async getHistory(): Promise<InterviewHistoryResponse> {
        const data = await apiClient.get<InterviewHistoryResponse>("/api/interviews/history");
        _historyCache = data;
        writeStoredHistory(data);
        return data;
    },

    getCachedHistory(): InterviewHistoryResponse | null {
        if (_historyCache) return _historyCache;
        const stored = readStoredHistory();
        if (stored) _historyCache = stored;
        return stored;
    },

    /**
     * List interviews (legacy method - maps to getHistory)
     * 
     * @deprecated Use getHistory instead
     */
    async list(): Promise<InterviewHistoryResponse> {
        return this.getHistory();
    },

    /**
     * Get full interview details
     * 
     * GET /api/interviews/{id}
     * 
     * Includes interview metadata, questions, and answers with analysis
     * 
     * @param id - Interview ID
     * @returns Full interview details
     */
    async getInterview(id: string): Promise<InterviewDetail> {
        return apiClient.get<InterviewDetail>(`/api/interviews/${id}`);
    },

    /**
     * Get interview (legacy method name)
     * 
     * @deprecated Use getInterview instead
     */
    async get(id: string): Promise<{ interview: any }> {
        const detail = await this.getInterview(id);
        return { interview: detail.interview };
    },

    /**
     * Get analytics for a specific interview
     * 
     * GET /api/interviews/{id}/analytics
     * 
     * @param id - Interview ID
     * @returns Performance analytics and insights
     */
    async getAnalytics(id: string): Promise<InterviewAnalytics> {
        return apiClient.get<InterviewAnalytics>(
            `/api/interviews/${id}/analytics`
        );
    },

    /**
     * Reuse/duplicate an existing interview
     * 
     * POST /api/interviews/reuse
     * 
     * @param payload - Interview ID to reuse
     * @returns New interview data
     */
    async reuseInterview(id: string): Promise<InterviewSetupResponse> {
        return apiClient.post<InterviewSetupResponse>(`/api/interviews/${id}/reuse`, {});
    },

    /**
     * Cancel an in-progress interview
     * 
     * POST /api/interviews/{id}/cancel
     * 
     * @param payload - Interview ID to cancel
     * @returns Confirmation message
     */
    async cancel_interview(payload: { interview_id: string }): Promise<{ message: string }> {
        return apiClient.post<{ message: string }>(
            `/api/interviews/${payload.interview_id}/cancel`,
            {}
        );
    },

    /**
     * Get WebRTC session token for video recording
     * 
     * POST /api/interviews/webrtc-session
     * 
     * @returns Ephemeral token for WebRTC
     */
    async getWebRTCSession(): Promise<{ ephemeral_token: string }> {
        return apiClient.post<{ ephemeral_token: string }>(
            "/api/interviews/webrtc-session",
            {}
        );
    },

    /**
     * Get session (auth check)
     * 
     * GET /api/auth/session
     * 
     * @returns Session data
     */
    async getSession(): Promise<{ session: string }> {
        return apiClient.get<{ session: string }>("/api/auth/session");
    },

    /**
     * Get the single latest completed session (Video or Simulation)
     * 
     * GET /api/interviews/latest
     */
    async getLatestInterview(): Promise<LatestInterviewResponse> {
        return apiClient.get<LatestInterviewResponse>("/api/interviews/latest");
    },
};
